import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { Field, SettingsCard, TextArea, TextInput, TimeInput, Toggle } from '@allrouted/ui/Field';
import { useUi } from '@allrouted/ui/Toaster';

/**
 * Customer notifications: a list of events, one open at a time.
 *
 * This screen was a trigger-by-channel grid of toggles. Theirs is a master list
 * and a detail pane, and the difference is not cosmetic -- an event carries a
 * subject, a body and a send time, and none of that fits in a checkbox. The
 * grid also offered SMS and voice columns we cannot send on, which made two
 * thirds of it a promise we do not keep.
 */

/**
 * Their nine events, in their order and under their names.
 *
 * Ours were seven, inferred from their API before the screen had been seen, and
 * two described events that do not exist. The three "scheduled" ones fire on a
 * clock rather than on something happening, which is why only they carry a send
 * time.
 */
const TRIGGERS = [
  {
    id: 'scheduled_in_2_days',
    label: 'Order is scheduled in 2 days',
    ahead: true,
    hint: 'Send a notification to customers two days before their scheduled order.',
  },
  {
    id: 'scheduled_tomorrow',
    label: 'Order is scheduled tomorrow',
    ahead: true,
    hint: 'Send a notification to customers the day before their scheduled order.',
  },
  {
    id: 'scheduled_today',
    label: 'Order is scheduled today',
    ahead: true,
    hint: 'Send a notification on the morning of the visit.',
  },
  {
    id: 'early',
    label: 'Order is early',
    ahead: false,
    hint: 'The technician is running ahead of the promised window.',
  },
  {
    id: 'delayed',
    label: 'Order is delayed',
    ahead: false,
    hint: 'The technician is running behind the promised window.',
  },
  {
    id: 'almost_there',
    label: 'Order is almost there',
    ahead: false,
    hint: 'Sent as the technician approaches.',
  },
  {
    id: 'canceled',
    label: 'Order is canceled',
    ahead: false,
    hint: 'The visit will not happen.',
  },
  {
    id: 'not_completed',
    label: 'Order is not completed',
    ahead: false,
    hint: 'The technician attended but could not finish the work.',
  },
  {
    id: 'completed',
    label: 'Order is completed',
    ahead: false,
    hint: 'The work is done.',
  },
] as const;

type TriggerId = (typeof TRIGGERS)[number]['id'];

/*
 * Mirrors KNOWN_HOTWORDS in the shared package. Duplicated deliberately: the
 * console would otherwise pull a server-side module in for one array, and this
 * list is only ever rendered as help text. The server remains the authority on
 * which ones actually resolve, and says so in the preview.
 */
const HOTWORDS = [
  'Order name',
  'Customer name',
  'Company name',
  'Address',
  'Date',
  'ETA',
  'ETA range',
  'Driver name',
  'Vehicle name',
  'Tracking link',
  'Phone number',
];

interface RuleRow extends Record<string, unknown> {
  trigger: string;
  channel: string;
  enabled: boolean;
  send_at_time: number | null;
  subject: string | null;
  body: string | null;
}

interface Preview {
  subject: string;
  body: string;
  unknown: string[];
  sampledFrom: string | null;
}

export function NotificationsPage({ territoryId }: { territoryId: string }) {
  const ui = useUi();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [openId, setOpenId] = useState<TriggerId>(TRIGGERS[0].id);
  const [draft, setDraft] = useState<RuleRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);

  const load = useCallback(async () => {
    const { rules: list } = await api.notifications(territoryId);
    setRules(list as RuleRow[]);
  }, [territoryId]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = rules.find((r) => r.trigger === openId && r.channel === 'email') ?? null;
  const meta = TRIGGERS.find((t) => t.id === openId)!;

  /*
   * Re-arm the draft when the selection changes or the rules reload. Keyed on
   * the fields rather than the object, which is a new reference on every fetch
   * and would throw away whatever was being typed.
   */
  const key = current
    ? `${current.trigger}|${current.enabled}|${current.send_at_time}|${current.subject}|${current.body}`
    : openId;
  useEffect(() => {
    setDraft(
      current
        ? { ...current }
        : {
            trigger: openId,
            channel: 'email',
            enabled: false,
            send_at_time: meta.ahead ? 64800 : null,
            subject: null,
            body: null,
          },
    );
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const save = async (over: Partial<RuleRow> = {}) => {
    if (!draft) return;
    const next = { ...draft, ...over };
    setSaving(true);
    try {
      await api.saveNotification(territoryId, {
        trigger: next.trigger,
        channel: 'email',
        enabled: next.enabled,
        sendAtTime: meta.ahead ? next.send_at_time : null,
        subject: next.subject,
        body: next.body,
      });
      await load();
      ui.notify('ok', 'Saved');
    } catch (err) {
      ui.notify('error', 'Could not save', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!draft) return;
    try {
      setPreview(
        await api.previewNotification(territoryId, {
          subject: draft.subject ?? '',
          body: draft.body ?? '',
        }),
      );
    } catch (err) {
      ui.notify('error', 'Could not preview', err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="split">
      <div className="side">
        <div className="side-head">
          <h2>Notifications</h2>
          <span className="count-pill">{TRIGGERS.length}</span>
        </div>
        <div className="side-list">
          {TRIGGERS.map((t) => {
            const rule = rules.find((r) => r.trigger === t.id && r.channel === 'email');
            return (
              <button
                key={t.id}
                type="button"
                className="notif-row"
                data-selected={t.id === openId ? 'true' : undefined}
                onClick={() => setOpenId(t.id)}
              >
                <b>{t.label}</b>
                <small data-on={rule?.enabled ? 'true' : undefined}>
                  {rule?.enabled ? 'Enabled' : 'Disabled'}
                </small>
              </button>
            );
          })}
        </div>
      </div>

      <div className="detail">
        {draft ? (
          <SettingsCard
            title={meta.label}
            description={meta.hint}
            editing
            saving={saving}
            onSave={() => void save()}
          >
            <div className="banner" data-tone="warn">
              <div>
                <b>Templates are stored; nothing sends yet</b>
                <p>
                  No mail transport is wired up. Enabling an event records the intent and does not
                  contact anyone.
                </p>
              </div>
            </div>

            <Field label="Enable notification">
              <Toggle
                checked={draft.enabled}
                onChange={(v) => {
                  setDraft({ ...draft, enabled: v });
                  void save({ enabled: v });
                }}
                label={`Enable ${meta.label}`}
              />
            </Field>

            {/* Only the clock-driven events carry a time; the rest fire on the event. */}
            {meta.ahead ? (
              <Field label="Notification time" hint="Territory-local">
                <TimeInput
                  seconds={draft.send_at_time ?? 64800}
                  onChange={(v) => setDraft({ ...draft, send_at_time: v })}
                />
              </Field>
            ) : null}

            <h3 className="section-label">Email</h3>
            <Field label="Subject">
              <TextInput
                value={draft.subject ?? ''}
                placeholder="We are preparing your order"
                onChange={(v) => setDraft({ ...draft, subject: v })}
              />
            </Field>
            <Field label="Text" wide>
              <TextArea
                value={draft.body ?? ''}
                rows={8}
                placeholder={'Hello @@Customer name@@, your order is planned for @@Date@@.'}
                onChange={(v) => setDraft({ ...draft, body: v })}
              />
            </Field>

            <p className="disclosure-note">
              Hotwords like <code>@@Order name@@</code> fill in with the customer&apos;s own
              information when the message is sent. One this system does not recognise is left
              visible in the message rather than blanked, so a typo is caught rather than quietly
              shipped to everybody.
            </p>
            <p className="disclosure-note">
              Available: {HOTWORDS.map((h) => `@@${h}@@`).join(' · ')}
            </p>

            <div className="row-actions">
              <button type="button" className="btn" onClick={() => void runPreview()}>
                Preview
              </button>
            </div>

            {preview ? (
              <div className="notif-preview">
                <em>
                  Preview
                  {preview.sampledFrom ? ` · against ${preview.sampledFrom}` : ' · sample data'}
                </em>
                <b>{preview.subject || '(no subject)'}</b>
                <pre>{preview.body}</pre>
                {preview.unknown.length > 0 ? (
                  <p className="notif-unknown">
                    Not recognised, and will be sent as written:{' '}
                    {preview.unknown.map((h) => `@@${h}@@`).join(', ')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </SettingsCard>
        ) : (
          <div className="empty">Choose an event.</div>
        )}
      </div>
    </div>
  );
}
